import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PnlAnalytics } from "./PnlAnalytics";
import { CircleNotch, CloudArrowUp, Plus, LockSimple, CheckCircle, Warning, DownloadSimple, Scales, Brain, Paperclip, Trash } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  draft: ["bg-zinc-100 text-zinc-600", "Draft — upload statements"],
  quoted: ["bg-indigo-50 text-indigo-700", "Quote ready"],
  blocked: ["bg-rose-50 text-rose-700", "HALTED — reconciliation variance"],
  categorizing: ["bg-amber-50 text-amber-700", "Categorizing…"],
  review: ["bg-amber-50 text-amber-700", "Needs your answers"],
  firm_review: ["bg-sky-50 text-sky-700", "CPA final review"],
  complete: ["bg-emerald-50 text-emerald-700", "P&L published"],
};

function NewEngagement({ onDone }) {
  const [f, setF] = useState({ name: "", entity_type: "1120S", industry: "", tax_year: new Date().getFullYear() - 1 });
  const create = useMutation({
    mutationFn: () => api.post("/pnl/engagements", f).then((r) => r.data),
    onSuccess: (d) => { toast.success("Engagement created"); onDone(d.id); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3" data-testid="pnl-new-form">
      <h3 className="text-sm font-medium text-zinc-900">New Instant P&L engagement</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input data-testid="pnl-name-input" placeholder="Engagement name (e.g. Acme LLC 2025)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="pnl-industry-input" placeholder="Industry (optional)" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} />
        <select data-testid="pnl-entity-select" className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm" value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })}>
          <option value="1120S">S Corporation (Form 1120-S)</option>
          <option value="1065">Partnership (Form 1065)</option>
          <option value="1120">C Corporation (Form 1120)</option>
        </select>
        <Input data-testid="pnl-year-input" type="number" value={f.tax_year} onChange={(e) => setF({ ...f, tax_year: Number(e.target.value) })} />
      </div>
      <Button data-testid="pnl-create-btn" size="sm" disabled={!f.name.trim() || create.isPending} onClick={() => create.mutate()}>
        {create.isPending ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} className="mr-1" />} Create engagement
      </Button>
    </div>
  );
}

function UploadForm({ engId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const ALLOWED = /\.(pdf|csv|tsv|xlsx|xls)$/i;
  const addFiles = (files) => {
    const list = [...files];
    const good = list.filter((f) => ALLOWED.test(f.name));
    if (list.length > good.length) toast.error("Only PDF, CSV or Excel bank statements are accepted");
    if (good.length) setItems((prev) => [...prev, ...good.map((f) => ({ file: f, account_name: "", opening_balance: "", closing_balance: "" }))]);
  };
  const setField = (i, k, v) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const uploadAll = async () => {
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (const it of items) {
      const fd = new FormData();
      fd.append("account_name", it.account_name);
      fd.append("opening_balance", it.opening_balance);
      fd.append("closing_balance", it.closing_balance);
      fd.append("file", it.file);
      try {
        const d = await api.post(`/pnl/engagements/${engId}/upload`, fd).then((r) => r.data);
        ok += 1;
        toast.success(d.statement ? `${it.file.name}: ${d.statement.line_count} lines counted${d.duplicates_excluded ? ` · ${d.duplicates_excluded} duplicates excluded` : ""}` : d.message);
      } catch (e) {
        failed.push(it);
        toast.error(`${it.file.name}: ${errText(e)}`);
      }
    }
    setItems(failed);
    setBusy(false);
    if (ok) qc.invalidateQueries({ queryKey: ["pnl-eng", engId] });
  };

  const ready = items.length > 0 && items.every((it) => it.account_name && it.opening_balance !== "" && it.closing_balance !== "");
  return (
    <div
      data-testid="pnl-upload-form"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files || []); }}
      className={`rounded-md border border-dashed p-3 space-y-2 transition-colors duration-150 ${dragging ? "border-emerald-500 bg-emerald-50/60" : "border-zinc-300"}`}>
      <p className="text-xs font-medium text-zinc-700">
        {dragging ? "Drop your statements here" : "Add statements (PDF / CSV / Excel) — drag & drop files here or choose them"}
      </p>
      <input ref={fileRef} type="file" multiple accept=".pdf,.csv,.tsv,.xlsx,.xls" className="hidden" data-testid="pnl-file-input"
        onChange={(e) => { addFiles(e.target.files || []); e.target.value = ""; }} />
      <button data-testid="pnl-choose-file-btn" onClick={() => fileRef.current?.click()} className="text-xs border border-zinc-200 rounded-md px-2.5 py-1.5 hover:bg-zinc-50 text-zinc-700">
        + Choose files…
      </button>
      {items.map((it, i) => (
        <div key={`${it.file.name}-${i}`} className="rounded-md bg-zinc-50 border border-zinc-200 p-2 space-y-1.5" data-testid={`pnl-upload-item-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono text-zinc-700 truncate max-w-[240px]">{it.file.name}</span>
            <button data-testid={`pnl-remove-item-${i}`} onClick={() => removeItem(i)} className="text-[10px] text-zinc-500 hover:text-rose-600">Remove</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            <Input data-testid={`pnl-account-input-${i}`} placeholder="Account name (e.g. Chase ••4821)" value={it.account_name} onChange={(e) => setField(i, "account_name", e.target.value)} className="h-8 text-xs" />
            <Input data-testid={`pnl-opening-input-${i}`} placeholder="Opening balance" value={it.opening_balance} onChange={(e) => setField(i, "opening_balance", e.target.value)} className="h-8 text-xs" />
            <Input data-testid={`pnl-closing-input-${i}`} placeholder="Closing balance" value={it.closing_balance} onChange={(e) => setField(i, "closing_balance", e.target.value)} className="h-8 text-xs" />
          </div>
        </div>
      ))}
      {items.length > 0 && (
        <Button data-testid="pnl-upload-btn" size="sm" className="h-7 text-xs" disabled={!ready || busy} onClick={uploadAll}>
          {busy ? <CircleNotch size={13} className="animate-spin mr-1" /> : <CloudArrowUp size={13} className="mr-1" />}
          Upload {items.length} file{items.length !== 1 ? "s" : ""} & count lines (free)
        </Button>
      )}
      {items.length > 0 && !ready && <p className="text-[10px] text-zinc-500">Enter the account name and the statement's opening & closing balances for each file — that powers the zero-variance reconciliation.</p>}
    </div>
  );
}

const Q_TEXT = {
  mixed: "Was this purchase 100% business, or does it include personal expenses?",
  high_value: "This is a large purchase — is it business equipment/materials, a normal business expense, or personal?",
  unknown: "What service or product was purchased?",
};

function ExceptionCard({ g, idx, total, categories, value, onChange, engId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const vkid = g.vendor_key.replace(/ /g, "-");
  const uploadReceipt = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Receipt too large (8 MB max)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("vendor_key", g.vendor_key);
    fd.append("file", file);
    try {
      await api.post(`/pnl/engagements/${engId}/receipts`, fd);
      toast.success(`Receipt "${file.name}" attached`);
      qc.invalidateQueries({ queryKey: ["pnl-exceptions", engId] });
    } catch (e) {
      toast.error(errText(e));
    }
    setUploading(false);
  };
  return (
    <div className="rounded-md bg-white border border-zinc-200 p-3 space-y-2" data-testid={`pnl-exception-${vkid}`}>
      <div className="flex items-center justify-between text-[10px] text-zinc-400 uppercase tracking-wide">
        <span>Question {idx + 1} of {total}</span>
        <span>{g.count} transaction{g.count !== 1 ? "s" : ""} · {fmt(g.total)}</span>
      </div>
      <p className="text-xs text-zinc-800">Vendor: <strong>“{g.sample}”</strong></p>
      <p className="text-xs text-zinc-600 font-medium">{Q_TEXT[g.question_type] || Q_TEXT.unknown}</p>
      {g.suggested && <p className="text-[10px] text-zinc-500">AI suggestion: {g.suggested} · {g.rule}</p>}
      <div className="space-y-1.5 text-xs">
        <label className="flex flex-wrap items-center gap-2 cursor-pointer">
          <input type="radio" name={`ans-${vkid}`} data-testid={`pnl-answer-business-${vkid}`} checked={v.answer_type === "business"} onChange={() => set({ answer_type: "business" })} />
          <span>100% Business expense</span>
          {v.answer_type === "business" && (
            <select data-testid={`pnl-answer-select-${vkid}`} className="h-7 w-full sm:w-72 rounded-md border border-zinc-200 bg-white px-2 text-xs"
              value={v.category || ""} onChange={(e) => set({ category: e.target.value })}>
              <option value="">Select a category…</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`ans-${vkid}`} data-testid={`pnl-answer-personal-${vkid}`} checked={v.answer_type === "personal"} onChange={() => set({ answer_type: "personal", category: undefined })} />
          <span>100% Personal (owner draw — excluded from P&L)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`ans-${vkid}`} data-testid={`pnl-answer-split-${vkid}`} checked={v.answer_type === "split"} onChange={() => set({ answer_type: "split", category: undefined })} />
          <span>Split business / personal (our CPA team will split it with you)</span>
        </label>
      </div>
      <Input data-testid={`pnl-answer-note-${vkid}`} placeholder="Optional note (e.g. printer paper and coffee supplies)" value={v.note || ""} onChange={(e) => set({ note: e.target.value })} className="h-8 text-xs" />
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.gif" className="hidden" data-testid={`pnl-receipt-input-${vkid}`}
          onChange={(e) => { uploadReceipt(e.target.files?.[0]); e.target.value = ""; }} />
        <button data-testid={`pnl-receipt-btn-${vkid}`} onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 text-[11px] text-zinc-600 border border-zinc-200 rounded-md px-2 py-1 hover:bg-zinc-50 disabled:opacity-50">
          {uploading ? <CircleNotch size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach receipt (optional)
        </button>
        {(g.receipts || []).map((r) => (
          <span key={r.id} className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid={`pnl-receipt-chip-${r.id}`}>
            <CheckCircle size={11} weight="fill" /> {r.filename}
          </span>
        ))}
      </div>
    </div>
  );
}

function ExceptionReview({ engId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pnl-exceptions", engId], queryFn: () => api.get(`/pnl/engagements/${engId}/exceptions`).then((r) => r.data) });
  const [answers, setAnswers] = useState({});
  const complete = Object.entries(answers).filter(([, v]) => v.answer_type && (v.answer_type !== "business" || v.category));
  const submit = useMutation({
    mutationFn: () => api.post(`/pnl/engagements/${engId}/answers`, { answers: complete.map(([vendor_key, v]) => ({ vendor_key, answer_type: v.answer_type, category: v.category, note: v.note || "" })) }).then((r) => r.data),
    onSuccess: (d) => { toast.success(d.status === "firm_review" ? "All answered — your CPA is doing the final review" : `${d.remaining_exceptions} question(s) remaining`); setAnswers({}); qc.invalidateQueries({ queryKey: ["pnl-eng", engId] }); qc.invalidateQueries({ queryKey: ["pnl-exceptions", engId] }); qc.invalidateQueries({ queryKey: ["pnl-pnl", engId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!data?.groups?.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="pnl-exception-review">
      <h4 className="text-sm font-medium text-amber-900 flex items-center gap-1.5"><Warning size={15} /> Quick clarifications — {data.groups.length} question{data.groups.length !== 1 ? "s" : ""} (~1 min)</h4>
      <p className="text-[11px] text-amber-800">Your answers are saved to your permanent client memory, so future uploads need even fewer questions.</p>
      {data.groups.map((g, i) => (
        <ExceptionCard key={g.vendor_key} g={g} idx={i} total={data.groups.length} categories={data.categories} engId={engId}
          value={answers[g.vendor_key]} onChange={(v) => setAnswers({ ...answers, [g.vendor_key]: v })} />
      ))}
      <Button data-testid="pnl-submit-answers-btn" size="sm" disabled={!complete.length || submit.isPending} onClick={() => submit.mutate()}>
        {submit.isPending ? <CircleNotch size={14} className="animate-spin" /> : <Brain size={14} className="mr-1" />} Submit {complete.length ? `${complete.length} answer${complete.length !== 1 ? "s" : ""}` : "answers"}
      </Button>
    </div>
  );
}

function PnlView({ engId }) {
  const { data: p } = useQuery({ queryKey: ["pnl-pnl", engId], queryFn: () => api.get(`/pnl/engagements/${engId}/pnl`).then((r) => r.data) });
  if (!p) return null;
  const op = p.lines.filter((l) => !l.nonoperating);
  const nonop = p.lines.filter((l) => l.nonoperating);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="pnl-statement">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-zinc-900">Tax P&L — Form {p.engagement.entity_type}, TY {p.engagement.tax_year}</h4>
        <div className="flex items-center gap-2">
          <a data-testid="pnl-binder-btn" href={`${API_BASE}/api/pnl/engagements/${engId}/binder`} className="flex items-center gap-1 text-xs text-white bg-zinc-900 border border-zinc-900 rounded-md px-2.5 py-1.5 hover:bg-zinc-700">
            <DownloadSimple size={13} /> Audit Binder PDF
          </a>
          <a data-testid="pnl-export-btn" href={`${API_BASE}/api/pnl/engagements/${engId}/export`} className="flex items-center gap-1 text-xs text-zinc-700 border border-zinc-200 rounded-md px-2.5 py-1.5 hover:bg-zinc-50">
            <DownloadSimple size={13} /> Excel workbook
          </a>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-zinc-400 border-b border-zinc-100"><th className="py-1">WP</th><th>Category</th><th>Form line</th><th className="text-right">Total</th><th className="text-right">Deductible</th></tr></thead>
        <tbody>
          {op.map((l) => (
            <tr key={l.category} className="border-b border-zinc-50" data-testid={`pnl-line-${l.wp_ref}`}>
              <td className="py-1.5 font-mono text-zinc-400">{l.wp_ref}</td>
              <td className="text-zinc-800">{l.category} <span className="text-zinc-300">({l.txn_count})</span></td>
              <td className="text-zinc-500">{l.form_line}</td>
              <td className="text-right font-mono">{fmt(l.total)}</td>
              <td className="text-right font-mono">{l.deductible_pct < 100 ? `${fmt(l.deductible)} (${l.deductible_pct}%)` : fmt(l.deductible)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 pt-2 border-t border-zinc-200 text-xs space-y-1">
        <p className="flex justify-between"><span className="text-zinc-500">Gross income</span><span className="font-mono font-medium" data-testid="pnl-gross-income">{fmt(p.gross_income)}</span></p>
        <p className="flex justify-between"><span className="text-zinc-500">Net income (tax basis)</span><span className="font-mono font-medium" data-testid="pnl-net-income">{fmt(p.net_income)}</span></p>
        {p.meals_addback > 0 && <p className="flex justify-between"><span className="text-zinc-500">50% meals add-back (M-1)</span><span className="font-mono">{fmt(p.meals_addback)}</span></p>}
      </div>
      {nonop.length > 0 && (
        <div className="mt-3 pt-2 border-t border-zinc-100">
          <p className="text-[10px] font-medium text-zinc-400 uppercase mb-1">Non-operating / preparer open items</p>
          {nonop.map((l) => <p key={l.category} className="text-[11px] text-zinc-500 flex justify-between"><span>{l.category} ({l.txn_count})</span><span className="font-mono">{fmt(l.total)}</span></p>)}
        </div>
      )}
      <p className="mt-3 text-[9px] text-zinc-400 italic">{p.legend}</p>
      {p.audit_log && (
        <div className="mt-3 pt-2 border-t border-zinc-100" data-testid="pnl-audit-log">
          <p className="text-[10px] font-medium text-zinc-400 uppercase mb-1">Processing audit log</p>
          <p className="text-[11px] text-zinc-500 flex justify-between"><span>Lines processed</span><span className="font-mono">{p.audit_log.lines_processed}</span></p>
          {p.audit_log.duplicates_excluded > 0 && <p className="text-[11px] text-zinc-500 flex justify-between"><span>Duplicates excluded</span><span className="font-mono">{p.audit_log.duplicates_excluded}</span></p>}
          {p.audit_log.exclusions.map((x) => (
            <p key={x.category} className="text-[11px] text-zinc-500 flex justify-between"><span>{x.count} × {x.reason}</span><span className="font-mono">{fmt(x.total)}</span></p>
          ))}
        </div>
      )}
    </div>
  );
}

function EngagementDetail({ engId, onBack }) {
  const qc = useQueryClient();
  const { data: e } = useQuery({ queryKey: ["pnl-eng", engId], queryFn: () => api.get(`/pnl/engagements/${engId}`).then((r) => r.data), refetchInterval: (q) => (q.state.data?.status === "categorizing" ? 3000 : false) });
  const checkout = useMutation({
    mutationFn: () => api.post(`/pnl/engagements/${engId}/checkout`, { origin_url: window.location.origin }).then((r) => r.data),
    onSuccess: (d) => {
      if (d.status === "blocked") {
        toast.error("Reconciliation HALT — no charge was made. See diagnostics.");
        qc.invalidateQueries({ queryKey: ["pnl-eng", engId] });
      } else {
        window.location.href = d.checkout_url;
      }
    },
    onError: (err) => toast.error(errText(err)),
  });
  const accept = useMutation({
    mutationFn: () => api.post(`/pnl/engagements/${engId}/accept`).then((r) => r.data),
    onSuccess: (d) => {
      if (d.status === "blocked") toast.error("Reconciliation HALT — see diagnostics");
      else if (d.client_exceptions) toast.success(`Reconciled to the penny · ${d.client_exceptions} quick question(s) for you`);
      else if (d.cpa_review_items) toast.success(`Reconciled & categorized — ${d.cpa_review_items} item(s) in CPA final review`);
      else toast.success("Reconciled & categorized — in CPA final review before publishing");
      qc.invalidateQueries({ queryKey: ["pnl-eng", engId] });
      qc.invalidateQueries({ queryKey: ["pnl-exceptions", engId] });
    },
    onError: (err) => toast.error(errText(err)),
  });
  if (!e) return <CircleNotch className="animate-spin text-zinc-400 mx-auto" size={20} />;
  const [badge, label] = STATUS_META[e.status] || STATUS_META.draft;
  const q = e.quote;
  return (
    <div className="space-y-4" data-testid="pnl-engagement-detail">
      <div className="flex items-center justify-between">
        <button data-testid="pnl-back-btn" onClick={onBack} className="text-xs text-zinc-500 hover:text-zinc-900">← All engagements</button>
        <span className={`text-[10px] px-2 py-1 rounded-sm ${badge}`} data-testid="pnl-status-badge">{label}</span>
      </div>
      <div>
        <h3 className="text-sm font-medium text-zinc-900">{e.name} <span className="text-zinc-400 font-normal">· Form {e.entity_type} · TY {e.tax_year}</span></h3>
      </div>

      {e.statements?.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <p className="text-xs font-medium text-zinc-700 mb-2">Statements</p>
          {e.statements.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-1 text-xs border-b border-zinc-50 last:border-0" data-testid={`pnl-stmt-${s.id}`}>
              <span className="text-zinc-700">{s.account_name} · {s.filename}</span>
              <span className="flex items-center gap-2">
                <span className="text-zinc-400">{s.line_count} lines{s.duplicates_excluded ? ` · ${s.duplicates_excluded} dupes excluded` : ""}</span>
                {s.recon_status === "passed" && <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle size={12} weight="fill" /> $0.00</span>}
                {s.recon_status === "failed" && <span className="text-rose-600 font-mono">Δ {fmt(s.variance)}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {["draft", "quoted", "blocked"].includes(e.status) && <UploadForm engId={engId} />}

      {q && ["quoted", "blocked"].includes(e.status) && (
        <div className="rounded-lg border border-zinc-900 bg-zinc-900 text-white p-4" data-testid="pnl-quote-card">
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-2 flex items-center gap-1"><Scales size={12} /> Itemized quote — locked before any processing</p>
          <div className="text-xs space-y-1">
            <p className="flex justify-between"><span>First {q.tier1_lines.toLocaleString()} lines × $0.99</span><span className="font-mono">{fmt(q.tier1_lines * q.tier1_rate)}</span></p>
            {q.tier2_lines > 0 && <p className="flex justify-between"><span>{q.tier2_lines.toLocaleString()} incremental lines × $0.49</span><span className="font-mono">{fmt(q.tier2_lines * q.tier2_rate)}</span></p>}
            <p className="flex justify-between border-t border-white/10 pt-1 font-medium"><span>Total ({q.billable_lines.toLocaleString()} billable lines · {fmt(q.blended_rate)}/line blended)</span><span className="font-mono" data-testid="pnl-quote-total">{fmt(q.total)}</span></p>
          </div>
          {e.paid ? (
            <>
              <div className="mt-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2 text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="pnl-paid-banner">
                <CheckCircle size={13} weight="fill" /> Payment received — you're all set to run the engine.
              </div>
              <Button data-testid="pnl-accept-btn" variant="secondary" size="sm" className="mt-2 w-full" disabled={accept.isPending} onClick={() => accept.mutate()}>
                {accept.isPending ? <CircleNotch size={14} className="animate-spin" /> : <LockSimple size={14} className="mr-1" />} Run reconcile-first engine
              </Button>
            </>
          ) : (
            <Button data-testid="pnl-pay-btn" variant="secondary" size="sm" className="mt-3 w-full" disabled={checkout.isPending} onClick={() => checkout.mutate()}>
              {checkout.isPending ? <CircleNotch size={14} className="animate-spin" /> : <LockSimple size={14} className="mr-1" />} Accept quote & pay {fmt(q.total)} securely
            </Button>
          )}
          <p className="text-[9px] text-zinc-500 mt-2">Reconciliation runs before checkout — if any statement fails the $0.00 variance gate, you are never charged. Secure payment by Stripe (test card 4242 4242 4242 4242).</p>
        </div>
      )}

      {e.status === "blocked" && e.recon?.filter((r) => r.status === "failed").map((r) => (
        <div key={r.account + r.filename} className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="pnl-recon-diagnostic">{r.diagnostic}</div>
      ))}

      {["review", "firm_review", "complete"].includes(e.status) && (
        <>
          {e.recon?.length > 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-[11px] text-emerald-800 flex items-center gap-1.5" data-testid="pnl-recon-passed">
              <CheckCircle size={14} weight="fill" /> Zero-variance reconciliation passed on {e.recon.length} statement(s) — every line proven complete before categorization.
            </div>
          )}
          {e.status === "review" && <ExceptionReview engId={engId} />}
          {e.status === "firm_review" && (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-[11px] text-sky-800" data-testid="pnl-firm-review-banner">
              All your answers are in. Our CPA team is doing the final review — you'll get an email the moment your P&L package is published.
            </div>
          )}
          <PnlView engId={engId} />
          {e.status === "complete" && <PnlAnalytics engId={engId} />}
        </>
      )}
      {e.status === "categorizing" && <p className="text-xs text-zinc-500 flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Categorizing with client memory → rules engine → AI…</p>}
    </div>
  );
}

function MemoryPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pnl-memory"], queryFn: () => api.get("/pnl/memory").then((r) => r.data) });
  const update = useMutation({
    mutationFn: ({ vk, category, note }) => api.put(`/pnl/memory/${encodeURIComponent(vk)}`, { category, note: note || "" }),
    onSuccess: () => { toast.success("Rule updated — applies to all future uploads"); qc.invalidateQueries({ queryKey: ["pnl-memory"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const remove = useMutation({
    mutationFn: (vk) => api.delete(`/pnl/memory/${encodeURIComponent(vk)}`),
    onSuccess: () => { toast.success("Rule removed — we'll ask again next time"); qc.invalidateQueries({ queryKey: ["pnl-memory"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!data?.rules?.length) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="pnl-memory-panel">
      <h4 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Brain size={15} /> Your vendor memory — {data.rules.length} learned rule{data.rules.length !== 1 ? "s" : ""}</h4>
      <p className="text-[11px] text-zinc-500 mt-0.5 mb-2">These rules auto-categorize matching vendors on every future upload. Edit or remove them anytime — you stay in control.</p>
      <div className="divide-y divide-zinc-50">
        {data.rules.map((r) => {
          const vkid = r.vendor_key.replace(/ /g, "-");
          return (
            <div key={r.vendor_key} className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`pnl-memory-row-${vkid}`}>
              <span className="text-xs text-zinc-800 flex-1 min-w-[140px]">“{r.vendor_key}”{r.note ? <span className="text-zinc-400"> · {r.note}</span> : null}</span>
              <select data-testid={`pnl-memory-select-${vkid}`} className="h-7 rounded-md border border-zinc-200 bg-white px-2 text-xs" value={r.category}
                onChange={(e) => update.mutate({ vk: r.vendor_key, category: e.target.value, note: r.note })}>
                {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button data-testid={`pnl-memory-delete-${vkid}`} onClick={() => remove.mutate(r.vendor_key)} className="text-zinc-400 hover:text-rose-600" title="Remove rule">
                <Trash size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PnlEngine() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const { data: list } = useQuery({ queryKey: ["pnl-list"], queryFn: () => api.get("/pnl/engagements").then((r) => r.data) });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("pnl_session_id");
    const eng = params.get("pnl_eng");
    if (params.get("pnl_cancel")) {
      toast.info("Payment canceled — your quote is saved and waiting.");
      if (eng) setOpenId(eng);
      window.history.replaceState({}, "", "/portal");
      return;
    }
    if (!sid || !eng) return;
    setOpenId(eng);
    window.history.replaceState({}, "", "/portal");
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const d = await api.get(`/pnl/payments/status/${sid}`).then((r) => r.data);
        if (d.payment_status === "paid") {
          toast.success("Payment received — running the reconcile-first engine…");
          qc.invalidateQueries({ queryKey: ["pnl-eng", eng] });
          try { await api.post(`/pnl/engagements/${eng}/accept`); } catch { /* already run */ }
          qc.invalidateQueries({ queryKey: ["pnl-eng", eng] });
          qc.invalidateQueries({ queryKey: ["pnl-exceptions", eng] });
          qc.invalidateQueries({ queryKey: ["pnl-list"] });
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) {
          toast.error("Payment did not complete — you were not charged.");
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 10) setTimeout(poll, 2500);
      else toast.error("Payment status check timed out — refresh this page in a moment.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (openId) return <EngagementDetail engId={openId} onBack={() => setOpenId(null)} />;
  return (
    <div className="space-y-4" data-testid="pnl-engine">
      <div className="rounded-lg border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-zinc-900">Instant P&L — Autonomous CPA Engine</h3>
            <p className="text-xs text-zinc-500 mt-1">Upload raw bank statements → zero-variance reconciliation → IRS-form-mapped P&L. $0.99/line first 1,000, $0.49 after — quoted before any work.</p>
          </div>
          <Button data-testid="pnl-new-btn" size="sm" onClick={() => setShowNew(!showNew)}><Plus size={14} className="mr-1" /> New</Button>
        </div>
      </div>
      {showNew && <NewEngagement onDone={(id) => { setShowNew(false); setOpenId(id); }} />}
      {(list || []).map((e) => {
        const [badge, label] = STATUS_META[e.status] || STATUS_META.draft;
        return (
          <button key={e.id} data-testid={`pnl-eng-row-${e.id}`} onClick={() => setOpenId(e.id)}
            className="w-full flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-4 hover:border-zinc-400 transition-colors duration-150 text-left">
            <div>
              <p className="text-sm text-zinc-800">{e.name}</p>
              <p className="text-[11px] text-zinc-400">Form {e.entity_type} · TY {e.tax_year}{e.quote ? ` · ${e.quote.billable_lines} lines · ${fmt(e.quote.total)}` : ""}</p>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-sm ${badge}`}>{label}</span>
          </button>
        );
      })}
      {!list?.length && !showNew && <p className="text-xs text-zinc-400 italic">No engagements yet — click New to start.</p>}
      <MemoryPanel />
    </div>
  );
}
